import { QueryObserverOptions, useQuery } from "react-query";
import { APIError, APIResponse, API_URL, get } from "../api";

export type CaseTicketStatus =
  | "OPEN"
  | "IN_REVIEW"
  | "PENDING_USER"
  | "PENDING_PARTNER"
  | "ESCALATED"
  | "RESOLVED"
  | "REJECTED"
  | "CLOSED";

export type CaseTicketPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type CaseTicket = {
  id: string;
  userId?: string;
  ticketNumber: string;
  category: string;
  subcategory?: string | null;
  subject: string;
  description?: string | null;
  status: CaseTicketStatus;
  priority: CaseTicketPriority;
  createdAt: string;
  updatedAt: string;
};

type TicketListPayload = {
  items: CaseTicket[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type TicketListResponse = APIResponse<TicketListPayload>;

export type UseCaseTicketsFilter = {
  status?: CaseTicketStatus;
  priority?: CaseTicketPriority;
  q?: string;
  page?: number;
  limit?: number;
};

function buildQuery(filters?: UseCaseTicketsFilter) {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.priority) params.set("priority", filters.priority);
  if (filters?.q) params.set("q", filters.q);
  if (filters?.page) params.set("page", String(filters.page));
  if (filters?.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function useCaseTickets(
  filters?: UseCaseTicketsFilter,
  options?: QueryObserverOptions<TicketListResponse, APIError>
) {
  const query = useQuery<TicketListResponse, APIError>({
    queryKey: ["userCaseTickets", filters],
    queryFn: () =>
      get(API_URL + `/case-management/tickets${buildQuery(filters)}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    ...options,
  });

  return {
    items: query.data?.data?.items ?? [],
    meta: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useCaseTickets;
